/**
 * Top-level viewer dispatcher.
 *
 * Reads `selectedPath` from the store, fetches a stat for it so we know
 * the kind, and renders the right viewer module. The viewer modules
 * themselves are lazy-loaded so a route that never opens a PDF never
 * pulls the PDF wrapper code. Same for video/audio/markdown.
 *
 * When nothing is selected, renders the canonical empty state (single
 * line of muted text — UI guidelines §7).
 */
import type { FsTreeNode } from '@chaos/types/fs';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

import { fsApi } from '../../lib/api/fs';
import { getViewerKind, type ViewerKind } from '../../lib/fs/viewer-kind';
import { useFileExplorerStore } from '../../stores/file-explorer-store';

const TextViewer = React.lazy(() =>
  import('./viewers/text-viewer').then((m) => ({ default: m.TextViewer })),
);
const MarkdownViewer = React.lazy(() =>
  import('./viewers/markdown-viewer').then((m) => ({ default: m.MarkdownViewer })),
);
const ImageViewer = React.lazy(() =>
  import('./viewers/image-viewer').then((m) => ({ default: m.ImageViewer })),
);
const PdfViewer = React.lazy(() =>
  import('./viewers/pdf-viewer').then((m) => ({ default: m.PdfViewer })),
);
const MediaViewer = React.lazy(() =>
  import('./viewers/media-viewer').then((m) => ({ default: m.MediaViewer })),
);
const BinaryViewer = React.lazy(() =>
  import('./viewers/binary-viewer').then((m) => ({ default: m.BinaryViewer })),
);
const DirectoryViewer = React.lazy(() =>
  import('./viewers/directory-viewer').then((m) => ({ default: m.DirectoryViewer })),
);

interface FileViewerProps {
  /** Optional override for the path to view; defaults to the store's selection. */
  path?: string | null;
}

export function FileViewer({ path: pathOverride }: FileViewerProps = {}) {
  const selectedPath = useFileExplorerStore((s) => s.selectedPath);
  const path = pathOverride ?? selectedPath;

  if (!path) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center">
        <p className="text-[12px] text-muted-foreground/70">Select a file to preview.</p>
      </div>
    );
  }

  return <FileViewerInner key={path} path={path} />;
}

function FileViewerInner({ path }: { path: string }) {
  const statQ = useQuery({
    queryKey: ['fs', 'stat', path],
    queryFn: () => fsApi.stat({ path }),
    staleTime: 30_000,
  });

  if (statQ.isLoading) {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <p className="text-[12px] text-muted-foreground">Loading…</p>
      </div>
    );
  }
  if (statQ.error || !statQ.data) {
    const message =
      statQ.error instanceof Error ? statQ.error.message : 'Could not access this path.';
    return (
      <div className="flex h-full items-center justify-center px-6">
        <p className="text-[12px] text-error">{message}</p>
      </div>
    );
  }

  const node = statQ.data;
  const kind = getViewerKind({
    name: node.name,
    ...(node.kind ? { kind: node.kind } : {}),
    mime: node.mime ?? null,
  });

  return (
    <React.Suspense
      fallback={
        <div className="flex h-full items-center justify-center px-6">
          <p className="text-[12px] text-muted-foreground">Loading viewer…</p>
        </div>
      }
    >
      {renderViewer(node, kind)}
    </React.Suspense>
  );
}

function renderViewer(node: FsTreeNode, kind: ViewerKind): React.ReactNode {
  switch (kind) {
    case 'directory':
      return <DirectoryViewer node={node} />;
    case 'markdown':
      return <MarkdownViewer node={node} />;
    case 'code':
    case 'text':
      return <TextViewer node={node} />;
    case 'image':
      return <ImageViewer node={node} />;
    case 'pdf':
      return <PdfViewer node={node} />;
    case 'video':
      return <MediaViewer node={node} kind="video" />;
    case 'audio':
      return <MediaViewer node={node} kind="audio" />;
    default:
      return <BinaryViewer node={node} />;
  }
}
